import React, { useState, useRef, useEffect } from "react";
import { RiRefreshLine, RiUpload2Line } from "react-icons/ri";
import Button from "../../Utility/Button/Button";
import GlobalFilter from "../../Utility/GlobalFilter/GlobalFilter";
import AddButton from "../../Utility/AddButton/AddButton";
import Typography from "../../Utility/Typography";
import ToggleSwitch from "../../Utility/ToggleSwitch/ToggleSwitch";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";

const TimeSheetFilter = ({
  filters,
  onFilterChange,
  filterOptions = {},
  onLogTime,
  onSync,
  onExportCsv = null,
  onExportXlsx = null,
  showTask = true,
  showProject = true,
  showTeam = false,
  showUser = false,
  dateRangeLabel: _dateRangeLabel,
  onPrevWeek: _onPrevWeek,
  onNextWeek: _onNextWeek,
  disableNext: _disableNext,
  handleFilterReset = () => {},
  applyFilters = () => {},
  /** Draft range for the filter panel only; applied on Filter button */
  dateRangeDraft = null,
  onDateRangeDraftChange,
  /** Lifted from parent so the panel does not reset when the filter subtree remounts */
  filterExpanded,
  onFilterExpandedChange,
  showNewestFirstToggle = false,
  newestFirst = false,
  onNewestFirstChange,
}) => {
  const { can } = usePermissions();
  const canCreateTimeEntry = can(PERMISSION_KEYS.TIME_ENTRY_CREATE);
  const { clients = [], projects = [], tasks = [], teams = [], users = [] } = filterOptions;
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [exportInProgress, setExportInProgress] = useState(false);
  const exportDropdownRef = useRef(null);

  const showExportMenu = typeof onExportCsv === "function" || typeof onExportXlsx === "function";

  const handleDateRangeDraftChange = (range) => {
    onDateRangeDraftChange?.(range);
  };

  const handleReset = () => {
    // Let parent reset handler set date range (e.g. today); do not clear draft first or jQuery picker stays stale
    handleFilterReset();
  };

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const runExport = async (fn) => {
    if (typeof fn !== "function" || exportInProgress) return;
    setExportInProgress(true);
    try {
      await fn();
    } finally {
      setExportInProgress(false);
      setExportDropdownOpen(false);
    }
  };

  const fields = [
    {
      type: "dropdown",
      name: "client",
      label: "Client",
      value: filters?.client || [],
      options: clients,
      placeholder: "All Clients",
      searchable: true,
      multiselect: true,
      onChange: onFilterChange,
      zIndex: true,
    },

    {
      type: "dropdown",
      name: "project",
      label: "Project",
      value: filters?.project || [],
      options: projects,
      placeholder: "All Projects",
      searchable: true,
      multiselect: true,
      onChange: onFilterChange,
      visible: showProject,
      zIndex: true,
    },

    {
      type: "dropdown",
      name: "task",
      label: "Task",
      value: filters?.task || [],
      options: tasks,
      placeholder: "All Tasks",
      searchable: true,
      multiselect: true,
      onChange: onFilterChange,
      visible: showTask,
      zIndex: true,
    },

    {
      type: "dropdown",
      name: "team",
      label: "Team",
      value: filters?.team || [],
      options: teams,
      placeholder: "All Teams",
      searchable: true,
      multiselect: true,
      onChange: onFilterChange,
      visible: showTeam,
      zIndex: true,
    },
    {
      type: "dropdown",
      name: "user",
      label: "User",
      value: filters?.user || [],
      options: users,
      placeholder: "All Users",
      searchable: true,
      multiselect: true,
      onChange: onFilterChange,
      visible: showUser,
      zIndex: true,
    },
    {
      type: "daterange",
      name: "dateRange",
      label: "Date range",
      value: dateRangeDraft,
      placeholder: "Filter by date",
      onChange: handleDateRangeDraftChange,
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-3"></div>
      <GlobalFilter
        listingFilter={filters}
        handleFilterChange={onFilterChange}
        handleFilterReset={handleReset}
        applyFilters={applyFilters}
        fields={fields}
        columns={3}
        defaultExpanded={false}
        expanded={filterExpanded}
        onExpandedChange={onFilterExpandedChange}
        expandOnApply={true}
      />

      <div className="flex flex-wrap items-center justify-end gap-2 mb-3">
        {showNewestFirstToggle && typeof onNewestFirstChange === "function" ? (
          <div className="flex items-center gap-2 mr-auto">
            <Typography variant="text2" className="text-text-primary whitespace-nowrap m-0 p-0">
              Newest first
            </Typography>
            <ToggleSwitch
              checked={!!newestFirst}
              onChange={onNewestFirstChange}
              size="sm m-0 p-0"
              aria-label="Sort newest first"
            />
          </div>
        ) : null}

        {/* sync button */}
        {onSync && (
          <Button
            variant="neutral"
            size="vsm"
            title="Sync"
            className="btn-reset square-btn"
            onClick={onSync}
          >
            <RiRefreshLine className="text-lg text-text-secondary" />
          </Button>
        )}

        {showExportMenu && (
          <div className="relative" ref={exportDropdownRef}>
            <Button
              variant="neutral"
              size="vsm"
              title="Export"
              className="btn-reset square-btn"
              data-tooltipp="Export"
              disabled={exportInProgress}
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
            >
              <RiUpload2Line className="text-lg text-text-secondary" />
            </Button>

            {exportDropdownOpen && (
              <div className="absolute right-0 mt-2 min-w-[13rem] max-w-[20rem] bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                {typeof onExportCsv === "function" && (
                  <Button
                    type="button"
                    disabled={exportInProgress}
                    onClick={() => runExport(onExportCsv)}
                    className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                  >
                    <Typography variant="span1">Export CSV</Typography>
                  </Button>
                )}
                {typeof onExportXlsx === "function" && (
                  <Button
                    type="button"
                    disabled={exportInProgress}
                    onClick={() => runExport(onExportXlsx)}
                    className="!w-full !flex !items-center !space-x-3 !px-4 !py-2.5 !text-sm !text-gray-700 hover:!bg-gray-50 !transition-colors focus:!ring-0 focus:!ring-offset-0 !border-0 !shadow-none !bg-transparent !font-normal !justify-start !rounded-none"
                  >
                    <Typography variant="span1">Export XLSX</Typography>
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {canCreateTimeEntry && typeof onLogTime === "function" ? (
          <AddButton onClick={onLogTime} tooltip="Log Time" />
        ) : null}
      </div>
    </>
  );
};

export default TimeSheetFilter;
